<div class="user_profile_container">
  <!-- Loading State -->
  <div v-if="is_loading" class="text-center py-5">
    <div class="spinner-border text-primary-dark spinner-border-sm" role="status"></div>
    <p class="text-xxs mt-2 text-muted">Loading user details...</p>
  </div>

  <!-- Error State -->
  <div v-else-if="error_message" class="alert alert-danger rounded-lg p-4 shadow-sm">
    <div class="d-flex align-items-center gap-3">
      <i class="bi bi-exclamation-circle-fill fs-5"></i>
      <div>
        <p class="text-xs mb-1">{{ error_message }}</p>
        <button @click="fetch_user_details" class="btn btn-sm btn-outline-danger text-xxs rounded-small">
          <i class="bi bi-arrow-clockwise me-1"></i>Retry
        </button>
      </div>
    </div>
  </div>

  <!-- User Details State -->
  <div v-else-if="user_details" class="user_profile_card">
    <!-- Header Section -->
    <div class="border rounded-lg mb-3 p-4 section-bg shadow-sm">
      <div class="d-flex justify-content-start align-items-start gap-3">
        <div class="d-flex gap-3 flex-grow-1">
          <div class="bg-primary-dark d-flex text-white fw-bold">
            {{ get_initials() }}
          </div>
          <div class="flex-grow-1">
            <h5 class="text-sm fw-bold mb-1">{{ get_display_value(user_details.full_name, 'Name not provided') }}</h5>
            <p class="text-xxs text-muted mb-2">{{ get_display_value(user_details.email, 'No email provided') }}</p>
            <div class="d-flex gap-2">
              <span :class="['status-badge', 'text-xxs', 'px-2', 'py-1', 'rounded-small', get_status_class(user_details.is_active, user_details.is_blocked)]">
                {{ user_details.is_blocked ? 'Blocked' : user_details.is_active ? 'Active' : 'Inactive' }}
              </span>
            </div>
          </div>
        </div>
        <button v-if="allow_edit && !is_editing" @click="start_editing" class="btn btn-link text-primary-dark p-0" title="Edit profile">
          <i class="bi bi-pencil-square fs-5"></i>
        </button>
        <div v-else-if="allow_edit && is_editing" class="d-flex gap-2">
          <button @click="cancel_editing" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark">Cancel</button>
          <button @click="save_changes" class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark" :disabled="is_saving">
            {{ is_saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Personal Information Section -->
    <div class="border rounded-lg mb-3 p-4 section-bg shadow-sm">
      <h6 class="text-xs fw-bold mb-3 text-capitalize d-flex align-items-center gap-2">
        <i class="bi bi-person-fill"></i>Personal Information
      </h6>
      <div class="row g-3">
        <div class="col-md-6">
          <div class="form-group">
            <label class="text-xxs fw-bold text-muted mb-2">First Name</label>
            <input
              type="text"
              :class="['form-control', 'form-control-sm', 'text-xxs', 'bg-white', 'border', 'py-2', 'rounded-lg', { 'is-invalid': validation_errors.first_name }]"
              v-model="edited_details.first_name"
              :disabled="!is_editing"
              placeholder="First name not provided"
            />
            <div v-if="validation_errors.first_name" class="invalid-feedback d-block text-xxs">
              {{ validation_errors.first_name }}
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="text-xxs fw-bold text-muted mb-2">Last Name</label>
            <input
              type="text"
              :class="['form-control', 'form-control-sm', 'text-xxs', 'bg-white', 'border', 'py-2', 'rounded-lg', { 'is-invalid': validation_errors.last_name }]"
              v-model="edited_details.last_name"
              :disabled="!is_editing"
              placeholder="Last name not provided"
            />
            <div v-if="validation_errors.last_name" class="invalid-feedback d-block text-xxs">
              {{ validation_errors.last_name }}
            </div>
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label class="text-xxs fw-bold text-muted mb-2">Email Address</label>
            <input
              type="email"
              :class="['form-control', 'form-control-sm', 'text-xxs', 'bg-white', 'border', 'py-2', 'rounded-lg', { 'is-invalid': validation_errors.email }]"
              v-model="edited_details.email"
              :disabled="!is_editing"
              placeholder="Email not provided"
            />
            <div v-if="validation_errors.email" class="invalid-feedback d-block text-xxs">
              {{ validation_errors.email }}
            </div>
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label class="text-xxs fw-bold text-muted mb-2">Phone Number</label>
            <input
              type="tel"
              :class="['form-control', 'form-control-sm', 'text-xxs', 'bg-white', 'border', 'py-2', 'rounded-lg', { 'is-invalid': validation_errors.phone_number }]"
              v-model="edited_details.phone_number"
              :disabled="!is_editing"
              placeholder="Phone number not provided"
            />
            <div v-if="validation_errors.phone_number" class="invalid-feedback d-block text-xxs">
              {{ validation_errors.phone_number }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="allow_edit" class="border rounded-lg p-4 section-bg shadow-sm">
      <h6 class="text-xs fw-bold mb-3 d-flex align-items-center gap-2">
        <i class="bi bi-shield-lock-fill"></i>Account Security
      </h6>
      <div v-if="!is_password_mode" class="d-flex justify-content-between align-items-center gap-3 flex-wrap">
        <p class="text-xxs text-muted mb-0">
          Use password reset mode to confirm your current password and set a new one securely.
        </p>
        <button
          type="button"
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          @click="open_password_mode"
        >
          Change Password
        </button>
      </div>

      <div v-else>
        <div class="row g-3">
          <div class="col-12">
            <label class="text-xxs fw-bold text-muted mb-2">Current Password</label>
            <div class="position-relative">
              <input
                :type="show_current_password ? 'text' : 'password'"
                :class="['form-control', 'form-control-sm', 'text-xxs', 'bg-white', 'border', 'py-2', 'rounded-lg', { 'is-invalid': password_form_errors.current_password }]"
                v-model="password_form.current_password"
                autocomplete="off"
                autocapitalize="off"
                autocorrect="off"
                spellcheck="false"
                name="current-password-manual-entry"
                placeholder="Enter your current password"
              />
              <i
                :class="show_current_password ? 'bi bi-eye' : 'bi bi-eye-slash'"
                class="position-absolute end-0 top-50 translate-middle-y me-3 text-secondary cursor-pointer"
                @click="toggle_current_password_visibility"
              ></i>
            </div>
            <div v-if="password_form_errors.current_password" class="invalid-feedback d-block text-xxs">
              {{ password_form_errors.current_password }}
            </div>
          </div>

          <div class="col-md-6">
            <label class="text-xxs fw-bold text-muted mb-2">New Password</label>
            <div class="position-relative">
              <input
                :type="show_new_password ? 'text' : 'password'"
                :class="['form-control', 'form-control-sm', 'text-xxs', 'bg-white', 'border', 'py-2', 'rounded-lg', { 'is-invalid': password_form_errors.new_password }]"
                v-model="password_form.new_password"
                autocomplete="new-password"
                placeholder="Enter a new password"
                @input="update_password_validation(password_form.new_password)"
              />
              <i
                :class="show_new_password ? 'bi bi-eye' : 'bi bi-eye-slash'"
                class="position-absolute end-0 top-50 translate-middle-y me-3 text-secondary cursor-pointer"
                @click="toggle_new_password_visibility"
              ></i>
            </div>
            <div v-if="password_form_errors.new_password" class="invalid-feedback d-block text-xxs">
              {{ password_form_errors.new_password }}
            </div>
          </div>

          <div class="col-md-6">
            <label class="text-xxs fw-bold text-muted mb-2">Confirm New Password</label>
            <div class="position-relative">
              <input
                :type="show_confirm_new_password ? 'text' : 'password'"
                :class="['form-control', 'form-control-sm', 'text-xxs', 'bg-white', 'border', 'py-2', 'rounded-lg', { 'is-invalid': password_form_errors.confirm_password }]"
                v-model="password_form.confirm_password"
                autocomplete="new-password"
                placeholder="Confirm your new password"
              />
              <i
                :class="show_confirm_new_password ? 'bi bi-eye' : 'bi bi-eye-slash'"
                class="position-absolute end-0 top-50 translate-middle-y me-3 text-secondary cursor-pointer"
                @click="toggle_confirm_new_password_visibility"
              ></i>
            </div>
            <div v-if="password_form_errors.confirm_password" class="invalid-feedback d-block text-xxs">
              {{ password_form_errors.confirm_password }}
            </div>
          </div>
        </div>

        <div class="mt-3 password-requirements">
          <p class="mb-2 text-dark text-xxs">Password requirements:</p>
          <ul class="list-unstyled mb-3">
            <li
              v-for="(is_valid, requirement) in password_validation"
              :key="requirement"
              class="mb-1 text-xxs"
            >
              <i :class="['bi', is_valid ? 'bi-check-circle-fill text-success me-1' : 'bi-circle text-muted me-1']"></i>
              <span :class="is_valid ? 'text-success' : 'text-muted'">
                {{ get_requirement_text(requirement) }}
              </span>
            </li>
          </ul>
        </div>

        <div class="d-flex justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
            :disabled="is_changing_password"
            @click="cancel_password_mode"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
            :disabled="is_changing_password"
            @click="change_password"
          >
            {{ is_changing_password ? 'Changing...' : 'Update Password' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div v-else class="alert alert-info rounded-lg p-4 shadow-sm">
    <p class="mb-0 text-xs text-muted">No user details available</p>
  </div>
</div>
